import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { connectAdminSocket } from '../socket';

// ============================================================================
// RefundPage — 환불 요청 관리
// ============================================================================

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const REASON_LABELS = {
  wrong_order: '잘못 주문',
  quality: '품질 불만',
  late: '제공 지연',
  other: '기타',
};

const STATUS_LABELS = {
  pending: { label: '대기', cls: 'badge-pending' },
  approved: { label: '승인', cls: 'badge-accepted' },
  processing: { label: '처리중', cls: 'badge-payment_pending' },
  completed: { label: '완료', cls: 'badge-success' },
  rejected: { label: '거부', cls: 'badge-cancelled' },
  failed: { label: '실패', cls: 'badge-cancelled' },
};

const TABS = [
  { key: 'pending', label: '대기' },
  { key: 'processing', label: '처리중' },
  { key: 'completed', label: '완료' },
  { key: 'rejected', label: '거부' },
];

let toastCounter = 0;

const RefundPage = () => {
  const { user } = useAuth();
  const storeId = user?.storeId || user?.store_id;

  const [tab, setTab] = useState('pending');
  const [refunds, setRefunds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'approve' | 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const socketRef = useRef(null);

  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);

  const load = useCallback(async () => {
    if (!storeId) { setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const statusFilter = tab === 'processing' ? 'processing' : tab;
      const res = await api.get('/admin/refunds', { params: { status: statusFilter } });
      let list = res.data?.refunds || [];
      if (tab === 'processing') {
        // Merge approved + processing + failed for the "처리중" tab
        const [a, f] = await Promise.all([
          api.get('/admin/refunds', { params: { status: 'approved' } }).catch(() => ({ data: { refunds: [] } })),
          api.get('/admin/refunds', { params: { status: 'failed' } }).catch(() => ({ data: { refunds: [] } })),
        ]);
        list = [...list, ...(a.data?.refunds || []), ...(f.data?.refunds || [])];
        list.sort((x, y) => (y.created_at || '').localeCompare(x.created_at || ''));
      }
      setRefunds(list);
    } catch (err) {
      addToast('환불 목록을 불러오지 못했습니다', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [storeId, tab, addToast]);

  useEffect(() => { load(); }, [load]);

  // Real-time updates
  useEffect(() => {
    if (!storeId) return;
    const sock = connectAdminSocket(storeId);
    socketRef.current = sock;
    const onRequest = () => { addToast('새 환불 요청이 도착했습니다', 'info'); load(); };
    const onUpdate = () => { load(); };
    sock.on('refund_request', onRequest);
    sock.on('refund_update', onUpdate);
    return () => {
      sock.off('refund_request', onRequest);
      sock.off('refund_update', onUpdate);
    };
  }, [storeId, load, addToast]);

  const openDetail = async (refund) => {
    setSelected(refund);
    setDetail(null);
    try {
      const res = await api.get(`/admin/refunds/${refund.id}`);
      setDetail(res.data);
    } catch {
      setDetail({ refund, payment: null, order: null, items: [] });
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
    setActionModal(null);
    setRejectReason('');
    setNotes('');
  };

  const approve = async () => {
    if (!selected) return;
    setIsProcessing(true);
    try {
      await api.post(`/admin/refunds/${selected.id}/approve`, { notes });
      addToast('환불이 승인되었습니다', 'success');
      closeDetail();
      load();
    } catch (err) {
      addToast(err.response?.data?.error || '승인에 실패했습니다', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const reject = async () => {
    if (!selected) return;
    if (!rejectReason.trim()) {
      addToast('거절 사유를 입력해주세요', 'error');
      return;
    }
    setIsProcessing(true);
    try {
      await api.post(`/admin/refunds/${selected.id}/reject`, { reason: rejectReason.trim() });
      addToast('환불 요청이 거절되었습니다', 'success');
      closeDetail();
      load();
    } catch (err) {
      addToast(err.response?.data?.error || '거절에 실패했습니다', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const counts = refunds.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="page-container">
      {/* Toasts */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`}>
            <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
          </div>
        ))}
      </div>

      <div className="page-header">
        <div>
          <h1 className="page-title">환불 관리</h1>
          <p className="page-subtitle">고객 환불 요청을 승인 또는 거절합니다</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {tab === t.key && counts[t.key] != null && (
              <span style={{ marginLeft: 6, fontSize: '0.72rem', opacity: 0.7 }}>({refunds.length})</span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="card"><div className="card-body">불러오는 중...</div></div>
      ) : refunds.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>환불 요청이 없습니다</h3>
            <p>새로운 요청이 오면 자동으로 표시됩니다.</p>
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  <th style={thStyle}>요청일시</th>
                  <th style={thStyle}>테이블</th>
                  <th style={thStyle}>금액</th>
                  <th style={thStyle}>사유</th>
                  <th style={thStyle}>상태</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {refunds.map((r) => {
                  const status = STATUS_LABELS[r.status] || { label: r.status, cls: 'badge-cancelled' };
                  return (
                    <tr
                      key={r.id}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => openDetail(r)}
                    >
                      <td style={tdStyle}>{formatDateTime(r.requested_at || r.created_at)}</td>
                      <td style={tdStyle}>{r.table_number ? `${r.table_number}번` : '-'}</td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{formatWon(r.amount)}</td>
                      <td style={tdStyle}>
                        <div>{REASON_LABELS[r.reason] || r.reason}</div>
                        {r.reason_detail && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                            {r.reason_detail.slice(0, 40)}{r.reason_detail.length > 40 ? '...' : ''}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span className={`badge ${status.cls}`}>{status.label}</span>
                      </td>
                      <td style={tdStyle}>
                        <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); openDetail(r); }}>
                          상세
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <>
          <div className="modal-overlay" onClick={closeDetail} />
          <div className="modal modal-lg" role="dialog" aria-label="환불 상세">
            <div className="modal-header">
              <div className="modal-title">환불 요청 상세</div>
              <button className="modal-close" onClick={closeDetail}>×</button>
            </div>
            <div className="modal-body">
              {!detail ? (
                <div>불러오는 중...</div>
              ) : (
                <>
                  <div style={summaryBoxStyle}>
                    <Row label="요청일" value={formatDateTime(selected.requested_at || selected.created_at)} />
                    <Row label="결제 ID" value={<code style={{ fontSize: '0.75rem' }}>{selected.payment_id}</code>} />
                    <Row label="금액" value={<strong>{formatWon(selected.amount)}</strong>} />
                    <Row label="사유" value={REASON_LABELS[selected.reason] || selected.reason} />
                    {selected.reason_detail && <Row label="상세" value={selected.reason_detail} />}
                    <Row
                      label="상태"
                      value={
                        <span className={`badge ${(STATUS_LABELS[selected.status] || {}).cls}`}>
                          {(STATUS_LABELS[selected.status] || {}).label || selected.status}
                        </span>
                      }
                    />
                    {selected.failure_reason && (
                      <Row label="실패/거절 사유" value={<span style={{ color: 'var(--danger)' }}>{selected.failure_reason}</span>} />
                    )}
                  </div>

                  {detail.order && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: 8 }}>주문 내역</div>
                      <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 12 }}>
                        {(detail.items || []).map((it) => (
                          <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '6px 0' }}>
                            <span>{it.menu_name} × {it.quantity}</span>
                            <span>{formatWon(it.subtotal)}</span>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                          <span>합계</span>
                          <span>{formatWon(detail.order.total_amount)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Action area */}
              {selected.status === 'pending' && actionModal !== 'reject' && actionModal !== 'approve' && (
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button className="btn btn-danger" style={{ flex: 1 }} onClick={() => setActionModal('reject')}>
                    거절
                  </button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setActionModal('approve')}>
                    승인
                  </button>
                </div>
              )}

              {actionModal === 'approve' && (
                <div style={{ marginTop: 20, padding: 16, background: 'var(--primary-light)', borderRadius: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>승인 확인</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
                    {formatWon(selected.amount)}을(를) 환불합니다. 결제사 취소 API가 호출됩니다.
                  </div>
                  <textarea
                    className="form-textarea"
                    placeholder="관리자 메모 (선택)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setActionModal(null)} disabled={isProcessing}>취소</button>
                    <button className="btn btn-primary" style={{ flex: 1 }} onClick={approve} disabled={isProcessing}>
                      {isProcessing ? '처리 중...' : '환불 승인'}
                    </button>
                  </div>
                </div>
              )}

              {actionModal === 'reject' && (
                <div style={{ marginTop: 20, padding: 16, background: 'var(--danger-light)', borderRadius: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>거절 사유</div>
                  <textarea
                    className="form-textarea"
                    placeholder="거절 사유를 입력해주세요 (고객에게 표시됩니다)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setActionModal(null)} disabled={isProcessing}>취소</button>
                    <button className="btn btn-danger" style={{ flex: 1 }} onClick={reject} disabled={isProcessing}>
                      {isProcessing ? '처리 중...' : '거절'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const thStyle = { textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 };
const tdStyle = { padding: '14px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' };
const summaryBoxStyle = { background: 'var(--bg)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 };

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: '0.85rem' }}>
    <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
    <span style={{ textAlign: 'right', maxWidth: '65%' }}>{value}</span>
  </div>
);

export default RefundPage;
